import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
  provideAppInitializer,
} from '@angular/core';
import { provideRouter, Router } from '@angular/router';

import {
  HttpClient,
  provideHttpClient,
  withInterceptors,
} from '@angular/common/http';
import { AuthConfig, AuthService, Employee } from '@mes-slalom/ui-common-lib';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { authInterceptor } from './interceptors/auth.interceptor';
import { loadingInterceptor } from './interceptors/loading.interceptor';
import { CoreStore } from './store/core.store';
import { GlobalErrorHandler } from './services/global-error.handler';
import { EmployeeService } from './services/employee.service';

async function initAuth() {
  const auth = inject(AuthService);
  const core = inject(CoreStore);
  const employeeService = inject(EmployeeService);
  await auth.handleAuthCallback();

  if (!localStorage.getItem('tokens')) {
    auth.loginStart();
    return;
  }

  const employee: Employee = auth.getEmployee();
  console.log('Authenticated user:', employee);
  const securityRoles = employee.securityRoles || [];

  if (!securityRoles.length) {
    try {
      const tokens = JSON.parse(localStorage.getItem('tokens') || '{}');
      const tokenPayload = JSON.parse(
        atob(tokens.idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
      );
      const employeeId = tokenPayload.impersonation || tokenPayload.employeeId;

      if (employeeId) {
        (employee as Employee & { employeeId?: string }).employeeId = employeeId;
        const employeeResponse = await employeeService.getEmployeeByEmployeeId(
          employeeId
        );
        employee.name = employeeResponse?.person?.firstName + ' ' + employeeResponse?.person?.lastName;
      }
    } catch (e) {
      console.warn('Could not decode token for employee lookup', e);
    }
  }

  core.setUser(employee);
  const roles = securityRoles.map((s: any) => s.name);
  core.setSecurityRoles(roles);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes),
    provideHttpClient(withInterceptors([authInterceptor, loadingInterceptor])),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    {
      provide: AuthService,
      useFactory: () => {
        const http = inject(HttpClient);
        const router = inject(Router);
        const authEndpoint = environment.authEndpoint.endsWith('/')
          ? environment.authEndpoint
          : `${environment.authEndpoint}/`;
        const config: AuthConfig = {
          authEndpoint,
          clientId: environment.clientId,
          redirectUri: environment.redirectUri,
        };
        return new AuthService(http, router, config);
      },
    },
    provideAppInitializer(initAuth),
  ],
};
